import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import useGetApi from "../../../util/useGetApi";
import usePortfolioIcon from "../../../hooks/usePortfolioIcon";
import PortfolioDialog from "../PortfolioDialog";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

const ANSWERS = {
  proceed: "I am happy with this portfolio.",
  recommendedGoals:
    "I would like a portfolio based on my goals and objectives.",
  recommendedRisk: "I would like a portfolio based on my risk profile.",
  choose: "I would like to select my own portfolio.",
};

const recordKeeper = "Voya"; // Placeholder until multiple record keepers are supported

export default function SelectPortfolio({
  advanceScreen,
  previousScreen, // eslint-disable-line no-unused-vars
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { formOptions, user } = useAppStore();
  const setSelectedPortfolioId = useAppStore(
    (state) => state.setSelectedPortfolioId,
  );
  const { answer, setAnswer } = useAnswer(screen.id);
  const { logout } = useGetApi();
  const navigate = useNavigate();

  async function handleSignOut() {
    await logout();
    navigate("/login", { replace: true });
  }
  const [portfolio, setPortfolio] = useState("");
  const [tempPortfolio, setTempPortfolio] = useState("");
  const [popoverContent, setPopoverContent] = useState("");
  const [selectedOption, setSelectedOption] = useState(""); // Track which radio is selected
  const [recommendedPortfolioName, setRecommendedPortfolioName] = useState("");

  // Initialize selectedOption from saved answer when component mounts.
  // The persisted form.questions answer is the source of truth — a numeric
  // value means the user picked their own portfolio in a prior session.
  useEffect(() => {
    console.log("Initializing selected option with answer:", answer);
    if (answer) {
      if (answer === "recommendedGoals" || answer === "recommendedRisk") {
        setSelectedOption(answer);
        updateValidation(true);
      } else if (answer === user.artesysPortfolioCode || answer === "proceed") {
        setSelectedOption("proceed");
        updateValidation(true);
      } else if (answer === "choose" || !isNaN(Number(answer))) {
        setSelectedOption("choose");
        if (!isNaN(Number(answer))) {
          setPortfolio(answer);
          setTempPortfolio(answer);
        }
        updateValidation(answer !== "choose");
      }
    } else if (value.length > 0) {
      updateValidation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-validate when portfolio is selected
  useEffect(() => {
    if (selectedOption === "choose" && portfolio) {
      console.log("Portfolio selected, enabling validation:", portfolio);
      updateValidation(true);
    }
  }, [portfolio, selectedOption, updateValidation, user]);

  function updateAnswer(value) {
    console.log("updated answer - current data", {
      value,
      tempPortfolio,
      portfolio,
    });
    setSelectedOption(value);

    if (value === "recommendedGoals" || value === "recommendedRisk") {
      setPortfolio("");
      setTempPortfolio("");
      setAnswer(value);
      updateValidation(true);
    } else if (value === "proceed") {
      setPortfolio("");
      setTempPortfolio("");
      setAnswer(value);
      // 1.5 displays the age-based recommendation (user.recommendedPortfolioId),
      // not the user's prior selection. Accepting "I am happy with this
      // portfolio" here means accepting the recommendation.
      setSelectedPortfolioId(user.recommendedPortfolioId);
      updateValidation(true);
    } else if (value === "choose") {
      setAnswer("choose");
      setTempPortfolio(portfolio);
      updateValidation(portfolio !== "");
    } else if (portfolio) {
      setAnswer(portfolio);
      setSelectedPortfolioId(portfolio);
      updateValidation(true);
    }
  }

  function savePortfolio() {
    setPortfolio(tempPortfolio);
    setAnswer(tempPortfolio);
    setSelectedPortfolioId(tempPortfolio);
    updateValidation(true);
  }

  function cancelPortfolio() {
    setTempPortfolio(portfolio);
  }
  const testSet = (portfolio) => {
    setTimeout(() => {
      setTempPortfolio(portfolio);
    }, 0);
  };

  const isSaveDisabled = useMemo(() => tempPortfolio === "", [tempPortfolio]);

  useEffect(() => {
    // formOptions.portfolios is an array, not an id-keyed map — look up by id.
    const port = formOptions.portfolios.find(
      (p) => p.id === user.recommendedPortfolioId,
    );
    console.log("useEffect to set popover content", {
      formOptions,
      user,
      port,
    });
    if (port) {
      setPopoverContent(port.description);
      setRecommendedPortfolioName(port.name);
    }
  }, [user, formOptions]);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:py-20 md:px-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="mb-4 text-3xl md:mb-5 md:text-4xl">
            Hi {user.fname}
            {recordKeeper
              ? `, we were able to verify your account with ${recordKeeper}!`
              : "!"}
          </h1>
          <p>
            Based on your age and expected retirement year, the recommended
            portfolio for you is:
          </p>
        </div>
        <div className="mt-8 mb-4">
          <span className="text-base font-bold text-gray-800 md:text-lg">
            {recommendedPortfolioName}
          </span>
          <Popover.Root>
            <Popover.Trigger asChild>
              <button className="relative ml-[.25em]">
                <FontAwesomeIcon
                  icon="fa-regular fa-circle-info"
                  className="text-[.944em] text-as-green-100"
                />
              </button>
            </Popover.Trigger>
            <Popover.Content
              sideOffset={5}
              className="w-64 rounded bg-white p-5  shadow-xl will-change-[transform,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-as-green-50 data-[side=bottom]:animate-slide-up-fade data-[side=left]:animate-slide-right-fade data-[side=right]:animate-slide-left-fade data-[side=top]:animate-slide-down-fade z-50"
            >
              <div className="z-50 text-xs">
                <p>{popoverContent}</p>
              </div>
              <Popover.Close
                className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full font-[inherit] text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close"
              >
                <Cross2Icon />
              </Popover.Close>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Root>
        </div>

        <img
          className="block w-64 h-auto mx-auto mb-3 md:mb-4 md:w-72"
          src={usePortfolioIcon(user.recommendedPortfolioId)}
        />
        <div className="flex items-center justify-center mb-10 gap-x-5 md:mb-14">
          <div className="flex items-center gap-x-2">
            <div className="w-6 h-3 bg-as-green-50"></div>
            <div className="text-sm font-bold text-black">Fixed Income</div>
          </div>
          <div className="flex items-center gap-x-2">
            <div className="w-6 h-3 bg-as-green-100"></div>
            <div className="text-sm font-bold text-black">Equities</div>
          </div>
        </div>

        <form>
          <div className="max-w-[37.125rem] mx-auto">
            {/* {showPreviousAnswer && (
              <p className="!mb-4">
                During your previous session, the path you chose was{" "}
                <b>{previousAnswer}</b>
              </p>
            )} */}

            <h2 className="mb-6 text-xl font-light md:text-2xl md:mb-8">
              How would you like to proceed?
            </h2>
            <RadioGroup.Root
              className="space-y-3"
              aria-label="How would you like to proceed?"
              value={selectedOption}
            >
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="proceed"
                  id="proceed"
                  onClick={() => updateAnswer("proceed")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="proceed"
                  >
                    {ANSWERS.proceed}
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="recommendedGoals"
                  id="recommendedGoals"
                  onClick={() => updateAnswer("recommendedGoals")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="recommendedGoals"
                  >
                    {ANSWERS.recommendedGoals}
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="recommendedRisk"
                  id="recommendedRisk"
                  onClick={() => updateAnswer("recommendedRisk")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="recommendedRisk"
                  >
                    {ANSWERS.recommendedRisk}
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="choose"
                  id="choose"
                  onClick={() => updateAnswer("choose")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="choose"
                  >
                    {ANSWERS.choose}
                  </label>
                  {selectedOption === "choose" && (
                    <Dialog.Root>
                      {portfolio === "" ? (
                        <Dialog.Trigger asChild>
                          <div
                            className="inline-flex mt-4 cursor-pointer btn btn--small btn--secondary"
                            role="button"
                            tabIndex={0}
                          >
                            Select Portfolio
                            <FontAwesomeIcon
                              icon="fa-solid fa-plus"
                              className="ml-2.5"
                            />
                          </div>
                        </Dialog.Trigger>
                      ) : (
                        <Dialog.Trigger asChild>
                          <div
                            className="btn btn--secondary mt-4 inline-flex !w-full flex-wrap justify-between gap-2 !py-4 !text-sm cursor-pointer"
                            role="button"
                            tabIndex={0}
                          >
                            <span className="text-left text-gray-800">
                              {formOptions.portfolios.find(
                                (p) => p.id === parseInt(portfolio),
                              )?.name || "Select a portfolio"}
                            </span>
                            <span className="font-medium text-right text-teal-800 underline hover:text-as-green-100">
                              Change
                            </span>
                          </div>
                        </Dialog.Trigger>
                      )}

                      <Dialog.Portal>
                        <Dialog.Overlay className="fixed inset-0 z-[100] animate-fade-in overflow-y-auto overflow-x-hidden bg-teal-900/80">
                          <div className="max-w-full wrapper">
                            <Dialog.Content
                              className="w-full max-w-6xl mx-auto my-12 bg-white rounded-md shadow-xl"
                              onInteractOutside={(event) =>
                                event.preventDefault()
                              }
                            >
                              <div className="p-6">
                                <Dialog.Title className="text-2xl text-gray-800">
                                  Select Portfolio
                                </Dialog.Title>
                                <Dialog.Description className="mt-2">
                                  Choose which portfolio makes sense for you.
                                </Dialog.Description>
                              </div>
                              <div className="px-6 pb-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                  <PortfolioDialog
                                    portfolio={tempPortfolio}
                                    setPortfolio={testSet}
                                    recommendedPortfolioName={
                                      recommendedPortfolioName
                                    }
                                  />
                                </div>
                              </div>
                              <div className="sticky bottom-0 p-6 bg-white border-t-2 shadow-sm border-t-gray-200">
                                <div className="flex gap-3 md:justify-end md:gap-5">
                                  <Dialog.Close asChild>
                                    <button
                                      className="btn btn--secondary btn--small"
                                      onClick={cancelPortfolio}
                                    >
                                      Cancel
                                    </button>
                                  </Dialog.Close>
                                  <Dialog.Close asChild>
                                    <button
                                      className="btn btn--primary btn--small"
                                      disabled={isSaveDisabled}
                                      onClick={savePortfolio}
                                    >
                                      Save
                                    </button>
                                  </Dialog.Close>
                                </div>
                              </div>
                            </Dialog.Content>
                          </div>
                        </Dialog.Overlay>
                      </Dialog.Portal>
                    </Dialog.Root>
                  )}
                </RadioGroup.Item>
              </div>
            </RadioGroup.Root>
            <div className="flex justify-center gap-3 mt-6 md:mt-10 md:gap-5">
              <button
                type="button"
                className="btn border border-red-300 bg-white text-red-600 hover:border-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:border-red-600 focus-visible:text-red-700 active:border-red-700 active:text-red-700"
                onClick={handleSignOut}
              >
                Sign Out
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={Boolean(validation) === false}
                onClick={() => {
                  console.log(
                    "Continue clicked, validation:",
                    validation,
                    "answer:",
                    answer,
                    "portfolio:",
                    portfolio,
                  );
                  advanceScreen();
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
